import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  MessageSquare,
  Database,
  Radio,
  ShoppingBag,
  Settings,
  Users,
  ChevronRight,
  BarChart3,
  BookOpen,
  UserCog,
  Play,
  Share2,
  FileText,
  GraduationCap,
  Sparkles,
  Wand2,
  ChevronsUpDown,
  Check,
  Wallet,
  ChevronDown,
  Search,
  CreditCard,
  Headphones,
  ShieldCheck,
  Contact,
  Inbox,
  ClipboardList,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/store/authStore';
import { useTeamStore } from '@/store/teamStore';
import { creditsService } from '@/services/credits.service';
import { getThemeColors, type SectionColor } from '@/utils/theme';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  color: SectionColor;
}

const navGroups: NavGroup[] = [
  {
    label: 'OVERVIEW',
    color: 'blue',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    ],
  },
  {
    label: 'BUILD',
    color: 'violet',
    items: [
      { name: 'Agents', href: '/agents', icon: Bot },
      { name: 'Skills', href: '/skills', icon: Sparkles },
      { name: 'Training', href: '/training', icon: GraduationCap },
      { name: 'Co-work', href: '/cowork', icon: Wand2 },
    ],
  },
  {
    label: 'KNOWLEDGE',
    color: 'emerald',
    items: [
      { name: 'Library', href: '/knowledge', icon: Database },
      { name: 'Documents', href: '/documents', icon: FileText },
      { name: 'Knowledge Map', href: '/knowledge-map', icon: Share2 },
    ],
  },
  {
    label: 'DEPLOY',
    color: 'amber',
    items: [
      { name: 'Channels', href: '/channels', icon: Radio },
      { name: 'Integrations', href: '/integrations', icon: ShoppingBag },
    ],
  },
  {
    label: 'SOCIAL',
    color: 'rose',
    items: [
      { name: 'Social Capture', href: '/social-capture', icon: Megaphone },
    ],
  },
  {
    label: 'MONITOR',
    color: 'rose',
    items: [
      { name: 'Inbox', href: '/inbox', icon: Inbox },
      { name: 'Conversations', href: '/conversations', icon: MessageSquare },
      { name: 'Contacts', href: '/contacts', icon: Contact },
      { name: 'Orders', href: '/orders', icon: ClipboardList },
      { name: 'Sim Bots', href: '/bots', icon: UserCog },
      { name: 'Simulations', href: '/simulations', icon: Play },
      { name: 'Usage', href: '/usage', icon: BarChart3 },
    ],
  },
];

const bottomNav: NavItem[] = [
  { name: 'Team', href: '/team', icon: Users },
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Support', href: '/support', icon: Headphones },
  { name: 'API Docs', href: '/docs', icon: BookOpen },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const { user } = useAuthStore();
  const { organizations, fetchOrganizations, switchOrganization } = useTeamStore();
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOrg = organizations.find((o) => o.id === user?.organization_id);

  useEffect(() => {
    fetchOrganizations();
    creditsService.getBalance().then(d => setCreditBalance(d.balance)).catch(() => {});
  }, [fetchOrganizations]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleGroup = (label: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const isActiveRoute = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const sidebarContent = (
    <>
        {/* Logo - pinned at top */}
        <div className="flex items-center gap-1.5 px-5 py-4 border-b-2 border-[#1a1a2e] shrink-0">
          <img src="/logo-icon.svg" alt="ChatCup" className="h-20 w-20" />
          <span className="text-2xl font-extrabold tracking-tight text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ChatCup</span>
        </div>

        {/* Scrollable content area — everything below the logo scrolls on small screens */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Search hint */}
          <div className="px-3 pt-3 pb-1">
            <button
              onClick={() => {
                // Trigger command palette via keyboard event
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                document.dispatchEvent(event);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:bg-gray-100 hover:text-gray-500 transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Search...</span>
              <kbd className="ml-auto text-[10px] font-mono bg-white border border-gray-200 rounded px-1 py-0.5 text-gray-400">⌘K</kbd>
            </button>
          </div>

          {/* Grouped Navigation */}
          <div className="px-3 py-2">
            <nav className="space-y-0.5">
              {navGroups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.label);
                const hasActiveItem = group.items.some(item => isActiveRoute(item.href));
                const theme = getThemeColors(group.color);

                return (
                  <div key={group.label} className="mb-1">
                    {/* Group header */}
                    <button
                      onClick={() => toggleGroup(group.label)}
                      className="w-full flex items-center justify-between px-3 py-1.5 mt-2 first:mt-0"
                    >
                      <span className={cn(
                        'text-[10px] font-semibold tracking-wider uppercase',
                        hasActiveItem ? theme.accent : 'text-gray-400'
                      )}>
                        {group.label}
                      </span>
                      <ChevronDown className={cn(
                        'w-3 h-3 text-gray-300 transition-transform duration-200',
                        isCollapsed && '-rotate-90'
                      )} />
                    </button>

                    {/* Group items */}
                    {!isCollapsed && (
                      <div className="space-y-0.5">
                        {group.items.map((item) => {
                          const active = isActiveRoute(item.href);
                          return (
                            <NavLink
                              key={item.name}
                              to={item.href}
                              onClick={onMobileClose}
                              className={() =>
                                cn(
                                  'group flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150',
                                  active
                                    ? `${theme.navActive} ${theme.navText} border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e]`
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                )
                              }
                            >
                              <item.icon className={cn(
                                'w-4 h-4 flex-shrink-0',
                                active ? theme.navIcon : 'text-gray-400 group-hover:text-gray-500'
                              )} />
                              <span className="flex-1 truncate">{item.name}</span>
                              {active && (
                                <ChevronRight className={cn('w-3.5 h-3.5', theme.accent)} />
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Bottom nav separator */}
              <div className="border-t-2 border-gray-200 my-2 mx-1" />

              {/* Bottom nav items */}
              {bottomNav.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onMobileClose}
                  className={() =>
                    cn(
                      'group flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150',
                      isActiveRoute(item.href)
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    )
                  }
                >
                  <item.icon className={cn(
                    'w-4 h-4 flex-shrink-0',
                    isActiveRoute(item.href) ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                  )} />
                  <span className="flex-1 truncate">{item.name}</span>
                  {isActiveRoute(item.href) && (
                    <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
                  )}
                </NavLink>
              ))}

              {/* Admin section — super admins only */}
              {user?.is_super_admin && (
                <>
                  <div className="border-t-2 border-gray-200 my-2 mx-1" />
                  <div className="px-3 py-1.5">
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-red-400">ADMIN</span>
                  </div>
                  <NavLink
                    to="/admin"
                    onClick={onMobileClose}
                    className={() =>
                      cn(
                        'group flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150',
                        isActiveRoute('/admin')
                          ? 'bg-red-50 text-red-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      )
                    }
                  >
                    <ShieldCheck className={cn(
                      'w-4 h-4 flex-shrink-0',
                      isActiveRoute('/admin') ? 'text-red-600' : 'text-gray-400 group-hover:text-gray-500'
                    )} />
                    <span className="flex-1 truncate">Admin Panel</span>
                    {isActiveRoute('/admin') && (
                      <ChevronRight className="w-3.5 h-3.5 text-red-400" />
                    )}
                  </NavLink>
                </>
              )}
            </nav>
          </div>

        </div>

        {/* Pinned bottom section — always stays at bottom */}
        <div className="shrink-0 border-t-2 border-[#1a1a2e]">
          {/* Credit Balance */}
          {creditBalance !== null && (
            <NavLink
              to="/billing"
              className="mx-3 mt-2 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:bg-blue-100 transition-colors"
            >
              <Wallet className="w-4 h-4 text-blue-600" />
              <span className="text-[11px] font-medium text-gray-500">Credits</span>
              <span className={cn(
                'ml-auto text-sm font-bold',
                creditBalance > 1 ? 'text-blue-700' : creditBalance > 0 ? 'text-yellow-600' : 'text-red-600'
              )}>
                ${creditBalance.toFixed(2)}
              </span>
            </NavLink>
          )}

          {/* Org Switcher */}
          <div className="px-3 pb-2 relative" ref={dropdownRef}>
            <button
              onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 border-2 border-[#1a1a2e] rounded-xl hover:bg-gray-50 transition-colors"
            >
              <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center text-blue-700 text-[10px] font-bold flex-shrink-0">
                {currentOrg?.name?.charAt(0).toUpperCase() || 'O'}
              </div>
              <span className="flex-1 text-left truncate text-[13px]">{currentOrg?.name || 'Organization'}</span>
              <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            </button>
            {orgDropdownOpen && organizations.length > 0 && (
              <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[3px_3px_0px_#1a1a2e] py-1 z-50">
                {organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => {
                      if (org.id !== user?.organization_id) {
                        switchOrganization(org.id);
                      } else {
                        setOrgDropdownOpen(false);
                      }
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50"
                  >
                    <span className="flex-1 text-left truncate">{org.name}</span>
                    {org.id === user?.organization_id && (
                      <Check className="w-3.5 h-3.5 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-40">
        <div className="flex flex-col h-full bg-white border-r-2 border-[#1a1a2e]">
          {sidebarContent}
        </div>
      </div>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-64 flex flex-col bg-white border-r-2 border-[#1a1a2e] shadow-[4px_0px_0px_#1a1a2e]">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
};
